import { BrandclubEventType, TrackingPixel } from "./types";

let debug = false;
/**
 * Universal-pixel instances already loaded + init'd, keyed by loader URL + init
 * args. A Set, not a single `initialized` boolean: a brand runs ONE TTD pixel
 * PER conversion (each with its own upixelId in `pixelInitArgs`), and a global
 * flag silently dropped every pixel after the first — so a second row mapped to
 * the conversion event never fired.
 */
const initializedPixels = new Set<string>();

const PROVIDER = 'TheTradeDesk';

const log = (...args: any[]) => {
  if (!debug) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info(...['[TTD]'].concat(args));
};

function initializeTDDPixel(brandId: string, pixelLoadUrl: string, initArgs: string[]) {
  // ponytail: two pixels sharing a loader URL inject the tag twice (the browser
  // serves the second from cache). Dedupe the loader by URL separately from the
  // per-pixel init() only if that duplicate ever costs something.
  const key = `${pixelLoadUrl}|${JSON.stringify(initArgs)}`;
  if (!brandId || !pixelLoadUrl || !initArgs?.length || initializedPixels.has(key)) {
    return;
  }
  initializedPixels.add(key);

  const loaderScript = document.createElement('script');
  loaderScript.type = 'text/javascript';
  loaderScript.src = pixelLoadUrl;
  document.body.appendChild(loaderScript);
  loaderScript.onload = () => {
    log('initializeTDDPixel', brandId, pixelLoadUrl, initArgs);
    const initScript = document.createElement('script');
    initScript.type = 'text/javascript';
    initScript.innerHTML = `ttd_dom_ready(function() {
            if (typeof TTDUniversalPixelApi === 'function') {
            var universalPixelApi = new TTDUniversalPixelApi();
            universalPixelApi.init(
                ${JSON.stringify(initArgs[0])},
                ${JSON.stringify(initArgs[1])},
                ${JSON.stringify(initArgs[2])}
            );
        }});`;
    document.body.appendChild(initScript);
  };
}

const tddPixel = {
  enableDebug() {
    debug = true;
  },
  track({ brandId, pixels, eventName, eventType, eventPayload }: {
    brandId: string;
    pixels: TrackingPixel[];
    eventName: string;
    eventType: BrandclubEventType;
    eventPayload: any;
  }) {
    const pixel = pixels?.find((item) => item.pixelProvider === PROVIDER);
    if (!pixel) {
      return;
    }
    log(JSON.stringify({ brandId, pixels, eventName, eventType, payload: eventPayload }));
    // TTD's model is one universal pixel PER conversion: the event that fires it
    // is chosen by the row's `eventMapping` (upstream in externalTracking), and
    // firing IS loading + init'ing that pixel. So there is nothing to branch on
    // here — the old per-eventType if/else called this exact line three ways.
    initializeTDDPixel(brandId, pixel?.pixelLoadUrl, pixel?.pixelInitArgs ? JSON.parse(pixel.pixelInitArgs) : []);
  },

  grantConsent() {
    if (!initialized) {
      log('Cannot grant consent - TTD Pixel not initialized');
      return;
    }
    // TTD doesn't have a specific consent API documented
    // Consent is handled at initialization level
    log('TTD Pixel consent granted (managed via initialization)');
  },

  revokeConsent() {
    if (!initialized) {
      log('Cannot revoke consent - TTD Pixel not initialized');
      return;
    }
    // TTD doesn't have a specific consent API documented
    // Consent is handled at initialization level
    log('TTD Pixel consent revoked (managed via initialization)');
  },
};

export default tddPixel;
